import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { SuppliedByDropdown } from 'components/LicenseGMetrixTableDialog/SuppliedByDropdown/SuppliedByDropdown';
import { useLicenseDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { LicenseGMetrixTable } from 'components/LicenseGMetrixTable/LicenseGMetrixTable';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { License, SupplierOption } from 'apis/LicenseApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Container, Row } from 'react-bootstrap';
import { useSuperState } from 'hooks';

import 'components/LicenseGMetrixTableDialog/license-gmetrix-table-dialog.scss';

export interface LicenseGMetrixTableDialogResult {
    licenses: License[];
}

export interface LicenseGMetrixTableDialogHandle {
    hide: () => void;
    show: () => Promise<LicenseGMetrixTableDialogResult | null>;
}

interface LicenseGMetrixTableDialogProps {
    highestRoleId: number;
    licenses: License[];
    setLicenses: React.Dispatch<React.SetStateAction<License[]>>;
}

export const LicenseGMetrixTableDialog = forwardRef<LicenseGMetrixTableDialogHandle, LicenseGMetrixTableDialogProps>((props, forwardedRef) => {

    const {
        highestRoleId,
        licenses,
        setLicenses
    } = props;

    const [state, setState] = useSuperState({
        isOpen: false,
        loadingGridData: false,
        searchTerm: '',
        selectedLicenses: [] as License[]
    });
    const { isOpen, loadingGridData, searchTerm, selectedLicenses } = state;
    const { formatMessage } = useIntl();
    const licenseDefaultLookupSearchFields = useLicenseDefaultLookupSearchFields();

    const resolverRef = useRef<((value: LicenseGMetrixTableDialogResult | null) => void) | null>(null);

    const settle = useCallback((value: LicenseGMetrixTableDialogResult | null) => {

        const resolve = resolverRef.current;
        resolverRef.current = null;
        setState({ isOpen: false, searchTerm: '', selectedLicenses: [] });
        resolve?.(value);

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(forwardedRef, () => ({
        hide: () => settle(null),
        show: () => new Promise<LicenseGMetrixTableDialogResult | null>(resolve => {

            resolverRef.current?.(null);
            resolverRef.current = resolve;
            setState({ isOpen: true });

        })
    }), [settle]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve pending promise on unmount.
    useEffect(() => () => {

        resolverRef.current?.(null);
        resolverRef.current = null;

    }, []);

    const updateLicenseSupplier = (supplier: SupplierOption, licenseId: number) => {

        const newSetOfLicenses = [...licenses];
        const licenseIndex = newSetOfLicenses.findIndex(license => license.Id === licenseId);
        newSetOfLicenses[licenseIndex].SuppliedBy = supplier.Id;
        newSetOfLicenses[licenseIndex].SuppliedByName = supplier.Name;
        setLicenses(newSetOfLicenses);

    };

    return (
        <GMetrixDialog
            actionButtonClick={() => {

                const selected = licenses.filter(license => selectedLicenses.find(selectedLicense => selectedLicense.Id === license.Id));
                settle({ licenses: selected });

            }}
            dialogLabel={(
                <div className="d-flex align-items-center">
                    <div>
                        <FormattedMessage defaultMessage="Add License(s)" id="add_licenses.c23238b0" />
                    </div>
                </div>
            )}
            actionButtonDisabled={selectedLicenses.length === 0}
            actionButtonText={formatMessage({ id: 'add_licenses.c23238b0', defaultMessage: 'Add License(s)' })}
            onCancel={() => settle(null)}
            show={isOpen}
        >
            <Container fluid className="student-gmetrix-table-dialog">
                <Row>
                    <Col className="pb-3">
                        <LicenseGMetrixTable
                            manuallyRenderLicenses
                            selectableRows
                            columnData={[
                                { field: 'Id', title: formatMessage({ id: 'license_id.87d2b466', defaultMessage: 'License ID' }) },
                                { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                                { field: 'LicenseTypeString', title: formatMessage({ id: 'type.b65913c1', defaultMessage: 'Type', description: 'Formulario' }) },
                                { field: 'MaxUses', title: formatMessage({ id: 'max_uses.5571019d', defaultMessage: 'Max Uses' }) },
                                { field: 'UsageDays', title: formatMessage({ id: 'usage_days.d9750693', defaultMessage: 'Usage Days', description: 'Formulario' }) },
                                { field: 'DateCreate', hidden: highestRoleId !== 256, title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' }) },
                                {
                                    cell: ({ dataItem }) => (
                                        <SuppliedByDropdown
                                            currentSupplier={{ Id: dataItem.SuppliedBy, Name: dataItem.SuppliedByName, Type: parseInt(dataItem.LicenseType) }}
                                            license={dataItem}
                                            licenses={licenses} setLicenses={setLicenses}
                                            supplierOptions={dataItem.SupplierOptions}
                                            updateLicenseSupplier={updateLicenseSupplier}
                                        />

                                    ),
                                    field: 'SuppliedByName',
                                    title: formatMessage({ id: 'supplier.a310b248', defaultMessage: 'Supplier' }),
                                    width: 200
                                }
                            ]}
                            advancedSearchFields={licenseDefaultLookupSearchFields}
                            allowExport={false}
                            defaultItemsPerPage={15}
                            licenses={licenses}
                            loadingGridData={loadingGridData}
                            onSelectedChange={licenses => setState({ selectedLicenses: licenses })}
                            searchTerm={searchTerm}
                            selectedLicenses={selectedLicenses}
                            setLicenses={setLicenses}
                            setLoadingGridData={((v: boolean) => setState({ loadingGridData: v })) as React.Dispatch<React.SetStateAction<boolean>>}
                            setSearchTerm={term => setState({ searchTerm: term })}
                            toggleColumns={false}
                        />
                    </Col>
                </Row>
            </Container>
        </GMetrixDialog>
    );

});

LicenseGMetrixTableDialog.displayName = 'LicenseGMetrixTableDialog';
